import { StackScreenProps } from "@react-navigation/stack";
import React from "react";
import { InvoiceArrivalRootParamList } from "../../../../types";
import {
  Button,
  DEFAULT_PAGE_WIDTH,
  View,
  Page,
  SearchBar,
  TextButton,
} from "@towersystems/roam-common-native";
import { SceneMap, TabView } from "react-native-tab-view";
import {
  OtherInvoiceArrivalsTabView,
  PendingInvoiceArrivalsTabView,
} from "./components";
import { SearchContext, useSearch } from "../../../../utilities/search";
import { Spacings } from "react-native-ui-lib";
import { useTranslation } from "react-i18next";
import { useListFilterContext } from "../../../../utilities/list-filter/use-list-filter-context";
import { InvoiceArrivalFilterValues } from "../../types";
import { useFocusEffect } from "@react-navigation/native";
import { AntDesign } from "@expo/vector-icons";
import { renderTabBar } from "../../../../components/TabBar";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { FlatListSearchableSkeleton } from "../../../../components/FlatListSearchableSkeleton";

const renderScene = SceneMap({
  pending: PendingInvoiceArrivalsTabView,
  other: OtherInvoiceArrivalsTabView,
});

export type InvoiceArrivalsLandingScreenProps = StackScreenProps<
  InvoiceArrivalRootParamList,
  "InvoiceArrivalsLandingScreen"
>;

export const InvoiceArrivalsLandingScreen = ({
  navigation,
}: InvoiceArrivalsLandingScreenProps) => {
  const [index, setIndex] = React.useState(0);
  const search = useSearch();
  const { t } = useTranslation("invoice_arrival");
  const { filterState } = useListFilterContext<InvoiceArrivalFilterValues>();
  const transitionEnded = useHasTransitionEnded(navigation);
  const { activeFiltersCount } = filterState;

  const [routes] = React.useState([
    { key: "pending", title: t("tabs.invoice_list.tab1") },
    { key: "other", title: t("tabs.invoice_list.tab2") },
  ]);

  const [dimensions, setDimensions] = React.useState({ width: 0, height: 0 });

  React.useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <Button
          label={t("button.create")}
          isAction
          onPress={() =>
            navigation.navigate("CreateInvoiceArrivalRootScreen", {
              screen: "CreateInvoiceArrivalScreen",
            })
          }
          size="compact"
        ></Button>
      ),
    });
  }, []);

  useFocusEffect(
    React.useCallback(() => {
      navigation.setOptions({
        headerTitle() {
          return (
            <TextButton
              iconStyle={{ marginRight: 0 }}
              icon={<AntDesign name="filter" size={20} />}
              onPress={() => {
                navigation.navigate("InvoiceArrivalFilterRootScreen", {
                  screen: "InvoiceArrivalFilterScreen",
                });
              }}
            >
              {t("common:filter", {
                count: activeFiltersCount,
              })}
            </TextButton>
          );
        },
      });
    }, [activeFiltersCount])
  );

  if (false == transitionEnded) {
    return (
      <Page>
        <FlatListSearchableSkeleton searchbar={true} />
      </Page>
    );
  }

  const maxWidth =
    dimensions.width > DEFAULT_PAGE_WIDTH
      ? DEFAULT_PAGE_WIDTH
      : dimensions.width;

  const tabContent =
    dimensions.width > 0 ? (
      <TabView
        renderTabBar={renderTabBar}
        navigationState={{ index, routes }}
        renderScene={renderScene}
        onIndexChange={setIndex}
        initialLayout={{
          width: maxWidth,
        }}
        style={{
          maxWidth: maxWidth,
          alignSelf: "center",
          width: "100%",
        }}
      />
    ) : undefined;

  return (
    <Page padding={0} fullWidth scrollable={false}>
      <SearchContext.Provider value={search}>
        <View
          style={{
            marginTop: Spacings.s6,
            maxWidth: DEFAULT_PAGE_WIDTH,
            width: "100%",
            alignSelf: "center",
            paddingHorizontal: Spacings.s6,
          }}
          onLayout={(event) => {
            const { width, height } = event.nativeEvent.layout;
            setDimensions({ width, height });
          }}
        >
          <SearchBar
            placeholder={t("common:search2")}
            value={search.query}
            onChange={search.setQuery}
          />
        </View>
        {tabContent}
      </SearchContext.Provider>
    </Page>
  );
};
